import {
  FiBook,
  FiBookOpen,
  FiActivity,
  FiGlobe,
  FiTrendingUp,
  FiMusic,
  FiCpu,
  FiFeather,
  FiTarget,
  FiAperture,
  FiDroplet,
  FiZap,
  FiCompass,
  FiTool,
  FiUsers,
  FiStar,
  FiAward,
  FiCode,
  FiHash,
  FiLayers,
  FiSun,
  FiCloud,
  FiHeart,
  FiMessageSquare,
} from "react-icons/fi";
import {
  TbMathSymbols,
  TbAlphabetLatin,
  TbAtom,
  TbPalette,
  TbWorld,
  TbHistory,
} from "react-icons/tb";

/**
 * Known subjects with specific icons and colors
 * These take precedence over the hash-based assignment
 * Add new subjects here as they become available
 */
const knownSubjects = {
  language: {
    icon: TbAlphabetLatin,
    colorScheme: {
      name: "violet",
      color: "text-violet-600",
      bgColor: "bg-violet-50",
      lightBgColor: "bg-violet-100",
      borderColor: "border-violet-200",
      gradientFrom: "from-violet-100",
      gradientTo: "to-violet-50",
    },
    displayName: "Language",
  },
  math: {
    icon: TbMathSymbols,
    colorScheme: {
      name: "orange",
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      lightBgColor: "bg-orange-100",
      borderColor: "border-orange-200",
      gradientFrom: "from-orange-100",
      gradientTo: "to-orange-50",
    },
    displayName: "Math",
  },
  science: {
    icon: TbAtom,
    colorScheme: {
      name: "cyan",
      color: "text-cyan-600",
      bgColor: "bg-cyan-50",
      lightBgColor: "bg-cyan-100",
      borderColor: "border-cyan-200",
      gradientFrom: "from-cyan-100",
      gradientTo: "to-cyan-50",
    },
    displayName: "Science",
  },
  history: {
    icon: TbHistory,
    colorScheme: {
      name: "amber",
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      lightBgColor: "bg-amber-100",
      borderColor: "border-amber-200",
      gradientFrom: "from-amber-100",
      gradientTo: "to-amber-50",
    },
    displayName: "History",
  },
  geography: {
    icon: TbWorld,
    colorScheme: {
      name: "teal",
      color: "text-teal-600",
      bgColor: "bg-teal-50",
      lightBgColor: "bg-teal-100",
      borderColor: "border-teal-200",
      gradientFrom: "from-teal-100",
      gradientTo: "to-teal-50",
    },
    displayName: "Geography",
  },
  art: {
    icon: TbPalette,
    colorScheme: {
      name: "pink",
      color: "text-pink-600",
      bgColor: "bg-pink-50",
      lightBgColor: "bg-pink-100",
      borderColor: "border-pink-200",
      gradientFrom: "from-pink-100",
      gradientTo: "to-pink-50",
    },
    displayName: "Art",
  },
  music: {
    icon: FiMusic,
    colorScheme: {
      name: "indigo",
      color: "text-indigo-600",
      bgColor: "bg-indigo-50",
      lightBgColor: "bg-indigo-100",
      borderColor: "border-indigo-200",
      gradientFrom: "from-indigo-100",
      gradientTo: "to-indigo-50",
    },
    displayName: "Music",
  },
  reading: {
    icon: FiBookOpen,
    colorScheme: {
      name: "sky",
      color: "text-sky-600",
      bgColor: "bg-sky-50",
      lightBgColor: "bg-sky-100",
      borderColor: "border-sky-200",
      gradientFrom: "from-sky-100",
      gradientTo: "to-sky-50",
    },
    displayName: "Reading",
  },
  writing: {
    icon: FiFeather,
    colorScheme: {
      name: "purple",
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      lightBgColor: "bg-purple-100",
      borderColor: "border-purple-200",
      gradientFrom: "from-purple-100",
      gradientTo: "to-purple-50",
    },
    displayName: "Writing",
  },
  apush: {
    icon: TbHistory,
    colorScheme: {
      name: "red",
      color: "text-red-600",
      bgColor: "bg-red-50",
      lightBgColor: "bg-red-100",
      borderColor: "border-red-200",
      gradientFrom: "from-red-100",
      gradientTo: "to-red-50",
    },
    displayName: "AP US History",
  },
  apwh: {
    icon: TbWorld,
    colorScheme: {
      name: "teal",
      color: "text-teal-600",
      bgColor: "bg-teal-50",
      lightBgColor: "bg-teal-100",
      borderColor: "border-teal-200",
      gradientFrom: "from-teal-100",
      gradientTo: "to-teal-50",
    },
    displayName: "AP World History",
  },
  apeuro: {
    icon: TbHistory,
    colorScheme: {
      name: "blue",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      lightBgColor: "bg-blue-100",
      borderColor: "border-blue-200",
      gradientFrom: "from-blue-100",
      gradientTo: "to-blue-50",
    },
    displayName: "AP European History",
  },
  chemistry: {
    icon: TbAtom,
    colorScheme: {
      name: "cyan",
      color: "text-cyan-600",
      bgColor: "bg-cyan-50",
      lightBgColor: "bg-cyan-100",
      borderColor: "border-cyan-200",
      gradientFrom: "from-cyan-100",
      gradientTo: "to-cyan-50",
    },
    displayName: "AP Chemistry",
  },
};

/**
 * Available icons pool - dynamically assigned for unknown subjects
 */
const iconPool = [
  FiBook,
  FiTrendingUp,
  FiActivity,
  FiGlobe,
  FiLayers,
  FiFeather,
  FiCpu,
  FiTarget,
  FiAperture,
  FiDroplet,
  FiZap,
  FiCompass,
  FiTool,
  FiUsers,
  FiStar,
  FiAward,
  FiCode,
  FiSun,
  FiCloud,
  FiHeart,
  FiMessageSquare,
  FiHash,
];

/**
 * Available color schemes pool - dynamically assigned based on subject name
 */
const colorSchemes = [
  {
    name: "blue",
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    lightBgColor: "bg-blue-100",
    borderColor: "border-blue-200",
    gradientFrom: "from-blue-100",
    gradientTo: "to-blue-50",
  },
  {
    name: "purple",
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    lightBgColor: "bg-purple-100",
    borderColor: "border-purple-200",
    gradientFrom: "from-purple-100",
    gradientTo: "to-purple-50",
  },
  {
    name: "green",
    color: "text-green-600",
    bgColor: "bg-green-50",
    lightBgColor: "bg-green-100",
    borderColor: "border-green-200",
    gradientFrom: "from-green-100",
    gradientTo: "to-green-50",
  },
  {
    name: "amber",
    color: "text-amber-600",
    bgColor: "bg-amber-50",
    lightBgColor: "bg-amber-100",
    borderColor: "border-amber-200",
    gradientFrom: "from-amber-100",
    gradientTo: "to-amber-50",
  },
  {
    name: "pink",
    color: "text-pink-600",
    bgColor: "bg-pink-50",
    lightBgColor: "bg-pink-100",
    borderColor: "border-pink-200",
    gradientFrom: "from-pink-100",
    gradientTo: "to-pink-50",
  },
  {
    name: "rose",
    color: "text-rose-600",
    bgColor: "bg-rose-50",
    lightBgColor: "bg-rose-100",
    borderColor: "border-rose-200",
    gradientFrom: "from-rose-100",
    gradientTo: "to-rose-50",
  },
  {
    name: "indigo",
    color: "text-indigo-600",
    bgColor: "bg-indigo-50",
    lightBgColor: "bg-indigo-100",
    borderColor: "border-indigo-200",
    gradientFrom: "from-indigo-100",
    gradientTo: "to-indigo-50",
  },
  {
    name: "orange",
    color: "text-orange-600",
    bgColor: "bg-orange-50",
    lightBgColor: "bg-orange-100",
    borderColor: "border-orange-200",
    gradientFrom: "from-orange-100",
    gradientTo: "to-orange-50",
  },
  {
    name: "red",
    color: "text-red-600",
    bgColor: "bg-red-50",
    lightBgColor: "bg-red-100",
    borderColor: "border-red-200",
    gradientFrom: "from-red-100",
    gradientTo: "to-red-50",
  },
  {
    name: "teal",
    color: "text-teal-600",
    bgColor: "bg-teal-50",
    lightBgColor: "bg-teal-100",
    borderColor: "border-teal-200",
    gradientFrom: "from-teal-100",
    gradientTo: "to-teal-50",
  },
  {
    name: "cyan",
    color: "text-cyan-600",
    bgColor: "bg-cyan-50",
    lightBgColor: "bg-cyan-100",
    borderColor: "border-cyan-200",
    gradientFrom: "from-cyan-100",
    gradientTo: "to-cyan-50",
  },
  {
    name: "yellow",
    color: "text-yellow-600",
    bgColor: "bg-yellow-50",
    lightBgColor: "bg-yellow-100",
    borderColor: "border-yellow-200",
    gradientFrom: "from-yellow-100",
    gradientTo: "to-yellow-50",
  },
];

/**
 * Simple hash function to consistently map subject names to indices
 * Same subject will always get the same icon and color
 */
const hashString = (str) => {
  let hash = 0;
  const normalized = str.toLowerCase().trim();
  for (let i = 0; i < normalized.length; i++) {
    const char = normalized.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

/**
 * Get subject configuration by subject name
 * First checks for known subjects with specific icons/colors
 * Falls back to hash-based assignment for unknown subjects
 * @param {string} subject - Subject name (case insensitive)
 * @returns {object} Subject configuration object
 */
export const getSubjectConfig = (subject) => {
  if (!subject) {
    // Default fallback
    return {
      icon: FiBook,
      color: "text-gray-600",
      bgColor: "bg-gray-50",
      lightBgColor: "bg-gray-100",
      borderColor: "border-gray-200",
      gradientFrom: "from-gray-100",
      gradientTo: "to-gray-50",
      name: "Unknown",
    };
  }

  const normalizedSubject = subject.toLowerCase().trim();

  // Check if this is a known subject with specific config
  if (knownSubjects[normalizedSubject]) {
    const config = knownSubjects[normalizedSubject];
    return {
      icon: config.icon,
      ...config.colorScheme,
      name: config.displayName,
    };
  }

  // Fall back to hash-based assignment for unknown subjects
  const hash = hashString(subject);

  // Select icon and color scheme based on hash
  const iconIndex = hash % iconPool.length;
  const colorIndex = hash % colorSchemes.length;

  const selectedIcon = iconPool[iconIndex];
  const selectedColorScheme = colorSchemes[colorIndex];

  // Capitalize subject name for display
  const displayName =
    subject.charAt(0).toUpperCase() + subject.slice(1).toLowerCase();

  return {
    icon: selectedIcon,
    ...selectedColorScheme,
    name: displayName,
  };
};

/**
 * Get all available color schemes
 * @returns {array} Array of color scheme names
 */
export const getAllColorSchemes = () => {
  return colorSchemes.map((scheme) => scheme.name);
};

/**
 * Get all available icons
 * @returns {array} Array of icon components
 */
export const getAllIcons = () => {
  return iconPool;
};

/**
 * Capitalize first letter of a string
 * @param {string} str - String to capitalize
 * @returns {string} Capitalized string
 */
export const capitalize = (str) => {
  if (!str) {
    return "";
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
};
